import {
  ICON_REGISTRY,
  isFrontendIconName,
} from "@/components/icons/iconRegistry";

import { isRichTextBrandColorToken } from "./richTextBrandColorTokens";
import cn from "classnames";

const INLINE_ICON_SIZES = [12, 16, 20, 24, 32] as const;

type InlineIconSize = (typeof INLINE_ICON_SIZES)[number];

const DEFAULT_INLINE_ICON_SIZE: InlineIconSize = 16;

const INLINE_ICON_ALIGN_OPTIONS = [
  "baseline",
  "middle",
  "top",
  "bottom",
  "left",
  "center",
  "right",
  "text-bottom",
] as const;

const DEFAULT_INLINE_ICON_ALIGN: InlineIconAlign = "text-bottom";

type InlineIconAlign = (typeof INLINE_ICON_ALIGN_OPTIONS)[number];

export function RichTextInlineIcon({ value }: RichTextInlineIconProps) {
  const name = value?.name;
  if (!name || !isFrontendIconName(name)) return null;

  const Icon = ICON_REGISTRY[name].Icon;
  const size = resolveInlineIconSize(value?.size);
  const token = value?.token;
  const colorStyle =
    typeof token === "string" && isRichTextBrandColorToken(token)
      ? { color: `var(--color-${token})` }
      : undefined;

  const align = resolveInlineIconAlign(value?.align);
  const alignClass = align ? `align-${align}` : "align-text-bottom";
  return (
    <Icon
      data-testid="rich-text-inline-icon"
      className={cn("inline-block", alignClass)}
      width={size}
      height={size}
      style={colorStyle}
      aria-hidden
    />
  );
}

export type RichTextInlineIconProps = {
  value?: {
    name?: string;
    size?: string | number;
    token?: string;
    align?: { value?: InlineIconAlign } | InlineIconAlign;
  };
};

function resolveInlineIconSize(
  size: string | number | undefined,
): InlineIconSize {
  const parsed =
    typeof size === "number" ? size : Number.parseInt(String(size ?? ""), 10);
  return (INLINE_ICON_SIZES as readonly number[]).includes(parsed)
    ? (parsed as InlineIconSize)
    : DEFAULT_INLINE_ICON_SIZE;
}

function resolveInlineIconAlign(
  align: { value?: InlineIconAlign } | InlineIconAlign | undefined,
): InlineIconAlign | undefined {
  if (!align) return undefined;
  // support either a raw align value or an object with a `value` field
  const parsed =
    typeof align === "object" && "value" in align
      ? (align as any).value
      : (align as InlineIconAlign);
  return INLINE_ICON_ALIGN_OPTIONS.some((opt) => opt === parsed)
    ? (parsed as InlineIconAlign)
    : DEFAULT_INLINE_ICON_ALIGN;
}
